import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '@nanostores/react';
import { activeRouteStore } from '@customerio/hydra-nav-state';
import { useSideNavState } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { useEssentialsOnboardingGate } from '../completion';
import { getImportSubmittedWorkspaceId } from './import-submitted-route';
import { persistReturnHomeHintDismissed, readReturnHomeHintDismissed } from './storage';

export type ReturnHomeHintDismissReason = 'home-click' | 'closed';

export type ReturnHomeHint = {
  isOpen: boolean;
  dismiss: (reason: ReturnHomeHintDismissReason) => void;
};

/** The import submission that armed the hint, and whether it has since been dismissed. */
type ArmedHint = {
  workspaceId: number;
  isDismissed: boolean;
};

/**
 * Opens once an onboarding-eligible person submits a CSV import in the embedded
 * imports wizard, and stays open until dismissed. Dismissal is remembered per
 * workspace, so the hint shows at most once per workspace.
 */
export function useReturnHomeHint(): ReturnHomeHint {
  const { track } = useAnalytics();
  const gate = useEssentialsOnboardingGate();
  const activeRoute = useStore(activeRouteStore);
  const currentWorkspaceId = useWorkspaceIdFromCurrentUrl();
  const isCollapsed = useSideNavState()?.isCollapsed === true;

  const [armed, setArmed] = useState<ArmedHint | null>(null);

  // The active route moves on once the import finishes, so the submission is
  // latched here rather than derived from the current route alone.
  const submittedWorkspaceId = getImportSubmittedWorkspaceId(activeRoute);
  if (submittedWorkspaceId != null && submittedWorkspaceId !== armed?.workspaceId) {
    setArmed({ workspaceId: submittedWorkspaceId, isDismissed: readReturnHomeHintDismissed(submittedWorkspaceId) });
  }

  const isOnboarding = gate.isEligible && !gate.isLoading && !gate.isComplete;
  const isOpen =
    armed != null && !armed.isDismissed && armed.workspaceId === currentWorkspaceId && isOnboarding && !isCollapsed;

  // The nav outlives SPA navigation, so the hint can arm again for another
  // workspace; one Viewed event per armed hint, not per mount.
  const trackedViewWorkspaceIdRef = useRef<number | null>(null);
  const openWorkspaceId = isOpen && armed != null ? armed.workspaceId : null;
  useEffect(() => {
    if (openWorkspaceId == null || trackedViewWorkspaceIdRef.current === openWorkspaceId) {
      return;
    }
    trackedViewWorkspaceIdRef.current = openWorkspaceId;
    void track('onboardingChecklist', 'returnHintViewed', {});
  }, [openWorkspaceId, track]);

  const dismiss = useCallback(
    (reason: ReturnHomeHintDismissReason) => {
      if (armed == null || armed.isDismissed) {
        return;
      }
      persistReturnHomeHintDismissed(armed.workspaceId);
      setArmed({ ...armed, isDismissed: true });
      void track('onboardingChecklist', 'returnHintDismissed', { Reason: reason });
    },
    [armed, track],
  );

  return { isOpen, dismiss };
}
